import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FunctionsHttpError } from '@supabase/supabase-js'
import { KILO } from '../../constants/KILO_THEME'
import { MATERIAL_ICONS } from '../../lib/materialIcons'
import { supabase } from '../../lib/supabaseClient'
import MaterialIcon from '../ui/MaterialIcon'

const DISMISS_STORAGE_KEY = 'kilo_device_connect_banner_dismissed'

type DeviceConnectProps = {
  onConnecting?: () => void
  variant?: 'banner' | 'panel'
  dismissible?: boolean
}

async function readFunctionError(error: unknown): Promise<string | null> {
  if (error instanceof FunctionsHttpError) {
    try {
      const payload = (await error.context.json()) as { error?: string }
      if (payload?.error) return payload.error
    } catch {
      /* ignore parse failure */
    }
  }
  if (error instanceof Error && error.message) return error.message
  return null
}

function isBannerDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISS_STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

const DeviceConnect = ({
  onConnecting,
  variant = 'panel',
  dismissible = variant === 'banner',
}: DeviceConnectProps) => {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [dismissed, setDismissed] = useState(() => dismissible && isBannerDismissed())

  useEffect(() => {
    if (dismissible) setDismissed(isBannerDismissed())
  }, [dismissible])

  async function handleConnect() {
    setLoading(true)
    setError(null)
    onConnecting?.()

    try {
      const redirectUrl = `${window.location.origin}/app?tab=coach&connected=true`
      const { data, error: invokeError } = await supabase.functions.invoke('vital-connect', {
        body: { redirect_url: redirectUrl },
      })

      if (data?.error) throw new Error(String(data.error))
      if (invokeError) {
        const detail = await readFunctionError(invokeError)
        throw new Error(detail ?? invokeError.message ?? t('coach.deviceConnect.requestFailed'))
      }
      if (!data?.link_web_url) throw new Error(t('coach.deviceConnect.missingLink'))

      window.location.href = data.link_web_url as string
    } catch (err) {
      const message = err instanceof Error ? err.message : t('coach.deviceConnect.connectFailed')
      setError(message)
      setLoading(false)
    }
  }

  function handleDismiss() {
    try {
      localStorage.setItem(DISMISS_STORAGE_KEY, '1')
    } catch {
      /* ignore storage failures */
    }
    setDismissed(true)
  }

  if (dismissed) return null

  if (variant === 'banner') {
    return (
      <div className="flex items-start gap-3 rounded-xl border border-kilo-line bg-kilo-card px-4 py-3">
        <MaterialIcon name={MATERIAL_ICONS.watchScreentime} hoverable size="md" className="mt-0.5 shrink-0" />
        <div className="min-w-0 flex-1">
          <p className={`text-sm font-semibold ${KILO.text.primary}`}>{t('coach.insights.noDeviceTitle')}</p>
          <p className={`mt-0.5 text-xs leading-snug ${KILO.text.muted}`}>
            {t('coach.insights.noDeviceDescription')}
          </p>
          {error ? <p className="mt-1 text-xs text-red-400">{error}</p> : null}
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1.5">
          <button
            type="button"
            onClick={() => void handleConnect()}
            disabled={loading}
            className="rounded-lg bg-kilo-accent px-3 py-1.5 text-xs font-bold text-black disabled:opacity-50"
          >
            {loading ? t('coach.deviceConnect.opening') : t('coach.deviceConnect.connectDevice')}
          </button>
          {dismissible ? (
            <button
              type="button"
              onClick={handleDismiss}
              className={`text-[10px] text-kilo-soft hover:text-kilo-text`}
              aria-label={t('coach.deviceConnect.dismiss')}
            >
              {t('coach.deviceConnect.dismiss')}
            </button>
          ) : null}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-3 py-2">
      <div className="flex items-start gap-3">
        <MaterialIcon name={MATERIAL_ICONS.watchScreentime} hoverable size="lg" className="shrink-0" />
        <div className="min-w-0 flex-1">
          <h3 className={`text-base font-semibold ${KILO.text.primary}`}>{t('coach.insights.noDeviceTitle')}</h3>
          <p className={`mt-1 text-sm leading-relaxed ${KILO.text.muted}`}>
            {t('coach.insights.noDeviceDescription')}
          </p>
        </div>
      </div>
      {error ? <p className="text-xs text-red-400">{error}</p> : null}
      <button
        type="button"
        onClick={() => void handleConnect()}
        disabled={loading}
        className="w-full rounded-xl bg-kilo-accent py-3 font-semibold text-black transition-colors hover:bg-kilo-accent-dark disabled:opacity-50"
      >
        {loading ? t('coach.deviceConnect.opening') : t('coach.deviceConnect.connectDevice')}
      </button>
    </div>
  )
}

export default DeviceConnect
